import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Loader2, CheckCircle, AlertCircle, Landmark } from 'lucide-react'
import type { BankDetails } from '@/services/groupService'

interface PaymentsTabProps {
  bankDetails: BankDetails
  onBankDetailsChange: (field: keyof BankDetails, value: string) => void
  hasChanges: boolean
  saving: boolean
  error: string | null
  success: boolean
  onSave: () => void
}

export function PaymentsTab({
  bankDetails,
  onBankDetailsChange,
  hasChanges,
  saving,
  error,
  success,
  onSave,
}: PaymentsTabProps) {
  return (
    <div className="space-y-6">
      {/* Bank Details Section */}
      <Card>
        <CardHeader className="pb-4">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center">
              <Landmark className="h-5 w-5 text-primary" />
            </div>
            <div>
              <CardTitle className="text-lg">Bank Details</CardTitle>
              <CardDescription>Payment details to display on season signups (not for payment processing)</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Account Name */}
            <div className="space-y-2">
              <Label htmlFor="bank-account-name" className="text-sm font-medium">Account Name</Label>
              <Input
                id="bank-account-name"
                value={bankDetails.accountName || ''}
                onChange={(e) => onBankDetailsChange('accountName', e.target.value)}
                placeholder="e.g., Badminton Group"
                className="h-11"
              />
            </div>

            {/* Sort Code */}
            <div className="space-y-2">
              <Label htmlFor="bank-sort-code" className="text-sm font-medium">Sort Code</Label>
              <Input
                id="bank-sort-code"
                value={bankDetails.sortCode || ''}
                onChange={(e) => onBankDetailsChange('sortCode', e.target.value)}
                placeholder="e.g., 12-34-56"
                className="h-11"
              />
            </div>

            {/* Account Number */}
            <div className="space-y-2">
              <Label htmlFor="bank-account-number" className="text-sm font-medium">Account Number</Label>
              <Input
                id="bank-account-number"
                value={bankDetails.accountNumber || ''}
                onChange={(e) => onBankDetailsChange('accountNumber', e.target.value)}
                placeholder="e.g., 12345678"
                className="h-11"
              />
            </div>

            {/* Payment Reference */}
            <div className="space-y-2">
              <Label htmlFor="bank-reference" className="text-sm font-medium">Suggested Reference</Label>
              <Input
                id="bank-reference"
                value={bankDetails.reference || ''}
                onChange={(e) => onBankDetailsChange('reference', e.target.value)}
                placeholder="e.g., League-{name}"
                className="h-11"
              />
            </div>
          </div>

          {/* International details - collapsed */}
          <details className="group">
            <summary className="cursor-pointer text-sm text-muted-foreground hover:text-foreground transition-colors">
              International payment details (IBAN/BIC)
            </summary>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4 pt-4 border-t">
              {/* IBAN */}
              <div className="space-y-2">
                <Label htmlFor="bank-iban" className="text-sm font-medium">IBAN</Label>
                <Input
                  id="bank-iban"
                  value={bankDetails.iban || ''}
                  onChange={(e) => onBankDetailsChange('iban', e.target.value)}
                  placeholder="e.g., GB82 WEST 1234 5698 7654 32"
                  className="h-11"
                />
              </div>

              {/* BIC */}
              <div className="space-y-2">
                <Label htmlFor="bank-bic" className="text-sm font-medium">BIC/SWIFT</Label>
                <Input
                  id="bank-bic"
                  value={bankDetails.bic || ''}
                  onChange={(e) => onBankDetailsChange('bic', e.target.value)}
                  placeholder="e.g., WESTGB2L"
                  className="h-11"
                />
              </div>
            </div>
          </details>

          {/* Notes */}
          <div className="space-y-2">
            <Label htmlFor="bank-notes" className="text-sm font-medium">Payment Notes</Label>
            <Textarea
              id="bank-notes"
              value={bankDetails.notes || ''}
              onChange={(e) => onBankDetailsChange('notes', e.target.value)}
              placeholder="e.g., Please include your name and 'Season Fee' as the payment reference"
              rows={2}
            />
            <p className="text-xs text-muted-foreground">
              These details will be shown to players when they register for seasons with a joining fee
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Info Card */}
      <Card className="bg-blue-50/50 dark:bg-blue-950/20 border-blue-200 dark:border-blue-800">
        <CardContent className="pt-6">
          <div className="flex gap-3">
            <div className="h-10 w-10 rounded-lg bg-blue-500/10 flex items-center justify-center shrink-0">
              <Landmark className="h-5 w-5 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <p className="font-medium text-blue-900 dark:text-blue-100">How payment details work</p>
              <p className="text-sm text-blue-700 dark:text-blue-300 mt-1">
                When you enable "Show Bank Details" on a season with a joining fee, these details will be displayed 
                to players during registration. Payments are handled outside the platform.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Error/Success Messages & Save */}
      <div className="space-y-4">
        {error && (
          <div className="rounded-xl bg-destructive/10 border border-destructive/20 p-4 flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-destructive/20 flex items-center justify-center shrink-0">
              <AlertCircle className="h-5 w-5 text-destructive" />
            </div>
            <div>
              <p className="font-medium text-destructive">Error saving settings</p>
              <p className="text-sm text-destructive/80">{error}</p>
            </div>
          </div>
        )}
        
        {success && (
          <div className="rounded-xl bg-green-500/10 border border-green-500/20 p-4 flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-green-500/20 flex items-center justify-center shrink-0">
              <CheckCircle className="h-5 w-5 text-green-600 dark:text-green-400" />
            </div>
            <div>
              <p className="font-medium text-green-600 dark:text-green-400">Settings saved</p>
              <p className="text-sm text-green-600/80 dark:text-green-400/80">Your payment details have been saved</p>
            </div>
          </div>
        )}

        {/* Save Button */}
        <div className="flex items-center justify-between pt-2">
          <p className="text-sm text-muted-foreground">
            {hasChanges ? (
              <span className="flex items-center gap-1.5">
                <span className="h-2 w-2 rounded-full bg-amber-500 animate-pulse" />
                You have unsaved changes
              </span>
            ) : (
              'All changes saved'
            )}
          </p>
          <Button
            onClick={onSave}
            disabled={saving || !hasChanges}
            size="lg"
            className="min-w-[140px]"
          >
            {saving ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              'Save Changes'
            )}
          </Button>
        </div>
      </div>
    </div>
  )
}
